import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient, useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  GraduationCap,
  Calendar as CalendarIcon,
  Sun,
  Moon,
  Settings,
  LogOut,
  CreditCard,
  ChevronRight,
  Target,
} from "lucide-react";
import { format, isSameDay, parseISO } from "date-fns";
import { motion } from "framer-motion";

const CalendarView = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [colleges, setColleges] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const fetchColleges = useCallback(async () => {
    try {
      const response = await apiClient.get("/colleges");
      setColleges(response.data);
    } catch (error) {
      console.error("Error fetching colleges:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColleges();
  }, [fetchColleges]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Get deadlines as dates for calendar highlighting
  const deadlineDates = colleges
    .filter((c) => c.deadline && c.deadline !== "Rolling")
    .map((c) => {
      try {
        return parseISO(c.deadline);
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  // Get colleges for selected date
  const collegesOnSelectedDate = colleges.filter((c) => {
    if (!c.deadline || c.deadline === "Rolling") return false;
    try {
      return isSameDay(parseISO(c.deadline), selectedDate);
    } catch {
      return false;
    }
  });

  // Custom day content to show deadline dots
  const modifiers = {
    deadline: deadlineDates,
  };

  const modifiersStyles = {
    deadline: {
      position: "relative",
    },
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
      in_progress: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
      not_started: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
    };
    const labels = {
      submitted: "Submitted",
      in_progress: "In Progress",
      not_started: "Not Started",
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono ${styles[status] || styles.not_started}`}>
        {labels[status] || "Not Started"}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="font-bold text-xl font-outfit">Admit List</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="text-foreground"
                onClick={() => navigate("/calendar")}
                data-testid="nav-calendar-btn"
              >
                Calendar
              </Button>
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => navigate("/categories")}
                data-testid="nav-categories-btn"
              >
                Categories
              </Button>
            </nav>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/subscription")} data-testid="subscription-menu-item">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-outfit mb-2">
            Deadline Calendar
          </h1>
          <p className="text-muted-foreground">
            View all your application deadlines at a glance.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card>
              <CardContent className="p-4 md:p-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  className="w-full"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4 w-full",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-lg font-semibold font-outfit",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100 rounded-full hover:bg-secondary",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse",
                    head_row: "flex w-full",
                    head_cell: "text-muted-foreground rounded-md w-full font-normal text-sm",
                    row: "flex w-full mt-2",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 w-full aspect-square",
                    day: "h-full w-full p-0 font-normal aria-selected:opacity-100 hover:bg-secondary rounded-lg transition-colors",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "bg-accent text-accent-foreground font-semibold",
                    day_outside: "text-muted-foreground opacity-50",
                    day_disabled: "text-muted-foreground opacity-50",
                    day_hidden: "invisible",
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const hasDeadline = deadlineDates.some((d) => isSameDay(d, date));
                      return (
                        <div className="relative flex items-center justify-center h-full w-full">
                          <span>{date.getDate()}</span>
                          {hasDeadline && (
                            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-rose-500" />
                          )}
                        </div>
                      );
                    },
                  }}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Selected Date Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-outfit">
                  <CalendarIcon className="w-5 h-5" />
                  {format(selectedDate, "MMMM d, yyyy")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {collegesOnSelectedDate.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto mb-3">
                      <CalendarIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground text-sm">
                      No deadlines on this date
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {collegesOnSelectedDate.map((college) => (
                      <div
                        key={college.college_id}
                        className="p-3 rounded-lg border border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/college/${college.college_id}`)}
                        data-testid={`calendar-college-${college.college_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium mb-1">{college.name}</h4>
                            <p className="text-xs text-muted-foreground mb-2">
                              {college.application_type}
                            </p>
                            {getStatusBadge(college.status)}
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Deadlines List */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base font-outfit">All Deadlines</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {colleges
                    .filter((c) => c.deadline && c.deadline !== "Rolling")
                    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
                    .map((college) => (
                      <div
                        key={college.college_id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                        onClick={() => {
                          try {
                            setSelectedDate(parseISO(college.deadline));
                          } catch {}
                        }}
                      >
                        <span className="text-sm font-medium truncate max-w-[150px]">
                          {college.name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground">
                          {format(parseISO(college.deadline), "MMM d")}
                        </span>
                      </div>
                    ))}
                  {colleges.filter((c) => c.deadline && c.deadline !== "Rolling").length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No deadlines set yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav p-2">
        <div className="flex items-center justify-around">
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/dashboard")}
            data-testid="mobile-nav-dashboard"
          >
            <GraduationCap className="w-5 h-5" />
            <span className="text-xs">Dashboard</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2 text-primary"
            onClick={() => navigate("/calendar")}
            data-testid="mobile-nav-calendar"
          >
            <CalendarIcon className="w-5 h-5" />
            <span className="text-xs">Calendar</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/categories")}
            data-testid="mobile-nav-categories"
          >
            <Target className="w-5 h-5" />
            <span className="text-xs">Categories</span>
          </Button>
          <Button
            variant="ghost"
            className="flex-1 flex flex-col items-center gap-1 h-auto py-2"
            onClick={() => navigate("/settings")}
            data-testid="mobile-nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default CalendarView;
